<script>
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';
import PageHeader from '../../SettingsSubPageHeader.vue';
import InstagramCapabilitiesForm from './InstagramCapabilitiesForm.vue';
import router from '../../../../index';
import { mapGetters } from 'vuex';

export default {
  name: 'InstagramSetup',
  components: {
    LoadingState,
    PageHeader,
    InstagramCapabilitiesForm,
  },
  setup() {
    const { accountId } = useAccount();
    return {
      accountId,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      isCreating: false,
      inboxName: '',
      authCode: null,
      capabilities: {
        dm_enabled: true,
        comments_enabled: true,
        stories_enabled: true,
        human_agent_enabled: true,
      },
    };
  },
  validations: {
    inboxName: { required },
  },
  computed: {
    hasAuthCode() {
      return !!this.authCode;
    },
    instagramAppId() {
      return window.chatwootConfig.igAppId;
    },
    redirectUri() {
      return `${window.location.origin}/instagram/callback`;
    },
    instagramAuthUrl() {
      const scopes = [
        'instagram_business_basic',
        'instagram_business_manage_messages',
      ];
      if (this.instagramManageCommentsEnabled) {
        scopes.push('instagram_business_manage_comments');
      }
      return `https://www.instagram.com/oauth/authorize?client_id=${this.instagramAppId}&redirect_uri=${encodeURIComponent(this.redirectUri)}&response_type=code&scope=${scopes.join(',')}&state=${this.accountId}`;
    },
    instagramManageCommentsEnabled() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        'instagram_manage_comments'
      );
    },
    ...mapGetters({
      globalConfig: 'globalConfig/get',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
  },
  mounted() {
    this.checkForAuthCode();
  },
  methods: {
    checkForAuthCode() {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      if (code) {
        this.authCode = code;
        const cleanUrl = window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      }
    },
    startLogin() {
      window.location.href = this.instagramAuthUrl;
    },
    async createChannel() {
      this.v$.$touch();
      if (this.v$.$invalid) return;

      this.isCreating = true;
      try {
        const data = await this.$store.dispatch(
          'inboxes/createInstagramChannel',
          {
            code: this.authCode,
            inbox_name: this.inboxName,
            capabilities: this.capabilities,
          }
        );

        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: data.id },
        });
      } catch (error) {
        useAlert(
          this.$t('INBOX_MGMT.ADD.INSTAGRAM_CHANNEL.API.ERROR_MESSAGE')
        );
        this.isCreating = false;
      }
    },
  },
};
</script>

<template>
  <div
    class="border border-slate-25 dark:border-slate-800/60 bg-white dark:bg-slate-900 min-h-full p-6 w-full max-w-full md:w-3/4 md:max-w-[75%] flex-shrink-0 flex-grow-0 self-start"
  >
    <!-- Step 0: Connect Instagram -->
    <div
      v-if="!hasAuthCode && !isCreating"
      class="flex flex-col items-center max-w-lg mx-auto pt-6"
    >
      <div class="ig-icon-wrapper mb-5">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="w-8 h-8 text-white"
        >
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
          <circle cx="12" cy="12" r="5" />
          <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
        </svg>
      </div>

      <PageHeader
        class="text-center items-center"
        :header-title="$t('INBOX_MGMT.ADD.INSTAGRAM_CHANNEL.TITLE')"
        :header-content="$t('INBOX_MGMT.ADD.INSTAGRAM_CHANNEL.DESC')"
      />

      <div
        class="flex flex-col gap-3 w-full mt-6 mb-8 p-4 rounded-lg bg-slate-25 dark:bg-slate-800/40"
      >
        <div class="flex items-center gap-3">
          <div
            class="flex items-center justify-center w-8 h-8 rounded-full bg-woot-50 dark:bg-woot-800/30 flex-shrink-0"
          >
            <fluent-icon
              icon="chat"
              size="16"
              class="text-woot-500 dark:text-woot-400"
            />
          </div>
          <span class="text-sm text-slate-700 dark:text-slate-200">
            {{ $t('INBOX_MGMT.ADD.INSTAGRAM_CHANNEL.FEATURES.DMS') }}
          </span>
        </div>
        <div v-if="instagramManageCommentsEnabled" class="flex items-center gap-3">
          <div
            class="flex items-center justify-center w-8 h-8 rounded-full bg-woot-50 dark:bg-woot-800/30 flex-shrink-0"
          >
            <fluent-icon
              icon="comment-note"
              size="16"
              class="text-woot-500 dark:text-woot-400"
            />
          </div>
          <span class="text-sm text-slate-700 dark:text-slate-200">
            {{ $t('INBOX_MGMT.ADD.INSTAGRAM_CHANNEL.FEATURES.COMMENTS') }}
          </span>
        </div>
        <div class="flex items-center gap-3">
          <div
            class="flex items-center justify-center w-8 h-8 rounded-full bg-woot-50 dark:bg-woot-800/30 flex-shrink-0"
          >
            <fluent-icon
              icon="mention"
              size="16"
              class="text-woot-500 dark:text-woot-400"
            />
          </div>
          <span class="text-sm text-slate-700 dark:text-slate-200">
            {{ $t('INBOX_MGMT.ADD.INSTAGRAM_CHANNEL.FEATURES.STORIES') }}
          </span>
        </div>
      </div>

      <button type="button" class="ig-connect-button" @click="startLogin">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="w-5 h-5"
        >
          <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
          <circle cx="12" cy="12" r="5" />
          <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
        </svg>
        {{ $t('INBOX_MGMT.ADD.INSTAGRAM_CHANNEL.CONNECT_BUTTON') }}
      </button>
    </div>

    <!-- Loading state -->
    <LoadingState
      v-else-if="isCreating"
      :message="$t('INBOX_MGMT.ADD.INSTAGRAM_CHANNEL.CREATING')"
    />

    <!-- Step 1: Configure capabilities + name -->
    <div v-else class="max-w-lg mx-auto pt-6 pb-6">
      <div
        class="flex items-center gap-3 p-3 mb-6 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/40"
      >
        <div
          class="flex items-center justify-center w-8 h-8 rounded-full bg-green-100 dark:bg-green-800/30 flex-shrink-0"
        >
          <fluent-icon
            icon="checkmark"
            size="16"
            class="text-green-600 dark:text-green-400"
          />
        </div>
        <span class="text-sm font-medium text-green-700 dark:text-green-300">
          {{ $t('INBOX_MGMT.ADD.INSTAGRAM_CHANNEL.CONNECTED_SUCCESS') }}
        </span>
      </div>

      <PageHeader
        :header-title="$t('INBOX_MGMT.ADD.INSTAGRAM_CHANNEL.SETUP_TITLE')"
        :header-content="$t('INBOX_MGMT.ADD.INSTAGRAM_CHANNEL.SETUP_SUBTITLE')"
      />

      <form class="mt-6" @submit.prevent="createChannel">
        <label :class="{ error: v$.inboxName.$error }">
          {{ $t('INBOX_MGMT.ADD.INSTAGRAM_CHANNEL.INBOX_NAME') }}
          <input
            v-model.trim="inboxName"
            type="text"
            :placeholder="
              $t('INBOX_MGMT.ADD.INSTAGRAM_CHANNEL.INBOX_NAME_PLACEHOLDER')
            "
            @input="v$.inboxName.$touch"
          />
          <span v-if="v$.inboxName.$error" class="message">
            {{ $t('INBOX_MGMT.ADD.INSTAGRAM_CHANNEL.INBOX_NAME_REQUIRED') }}
          </span>
        </label>

        <div class="mt-6">
          <InstagramCapabilitiesForm v-model="capabilities" />
        </div>

        <div class="mt-4">
          <woot-submit-button
            :loading="isCreating"
            :button-text="
              $t('INBOX_MGMT.ADD.INSTAGRAM_CHANNEL.SUBMIT_BUTTON')
            "
          />
        </div>
      </form>
    </div>
  </div>
</template>

<style scoped>
.ig-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: linear-gradient(135deg, #f58529, #dd2a7b, #8134af, #515bd4);
  box-shadow: 0 4px 14px rgba(221, 42, 123, 0.3);
}

.ig-connect-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #f58529, #dd2a7b, #8134af);
  cursor: pointer;
  transition: opacity 0.15s ease, transform 0.15s ease, box-shadow 0.15s ease;
  box-shadow: 0 2px 8px rgba(221, 42, 123, 0.25);
}

.ig-connect-button:hover {
  opacity: 0.92;
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(221, 42, 123, 0.35);
}

.ig-connect-button:active {
  transform: translateY(0);
  opacity: 0.85;
}
</style>
